import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer, Download, X } from 'lucide-react';
import { SettlementCalculationResult } from '@/engine/settlement_calculator';
import { ProductionCycleRecord, SettlementRecord } from '@/types/database';
import { formatCurrency, formatDate } from '@/lib/utils';

interface SettlementStatementPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  cycle: ProductionCycleRecord;
  settlement?: SettlementRecord | null;
  calculationResult: SettlementCalculationResult;
}

// Convert numbers to words for formal liquidation statement
function numberToWords(num: number): string {
  if (num === 0) return 'Zero Pesos Only';
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const absNum = Math.abs(num);
  const pesos = Math.floor(absNum);
  const centavos = Math.round((absNum - pesos) * 100);

  function convertHundreds(n: number): string {
    let str = '';
    if (n >= 100) {
      str += units[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      str += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) {
      str += units[n] + ' ';
    }
    return str.trim();
  }

  let words = '';
  const millions = Math.floor(pesos / 1000000);
  const thousands = Math.floor((pesos % 1000000) / 1000);
  const remainder = pesos % 1000;

  if (millions > 0) {
    words += convertHundreds(millions) + ' Million ';
  }
  if (thousands > 0) {
    words += convertHundreds(thousands) + ' Thousand ';
  }
  if (remainder > 0) {
    words += convertHundreds(remainder) + ' ';
  }

  words = words.trim() + ' Pesos';

  if (centavos > 0) {
    words += ` and ${centavos}/100 Centavos`;
  }
  words += ' Only';

  if (num < 0) {
    words = 'Negative ' + words;
  }
  return words;
}

export const SettlementStatementPrintModal: React.FC<SettlementStatementPrintModalProps> = ({
  isOpen,
  onClose,
  cycle,
  settlement,
  calculationResult,
}) => {
  const handlePrint = () => {
    window.print();
  };

  const settlementNumber = settlement?.settlement_number || `STL-${new Date().getFullYear()}-${cycle.cycle_code}`;
  const liquidationDate = settlement?.settlement_date ? formatDate(settlement.settlement_date) : formatDate(new Date().toISOString());
  const growerName = cycle.growers?.full_name || cycle.legacy_raiser_name || 'Grower Partner';
  const farmLocation = `${cycle.farms?.farm_name || 'Sorosoro Ibaba'} ${cycle.production_units?.unit_code ? `/ Unit ${cycle.production_units.unit_code}` : ''}`;
  const contractRef = calculationResult.profit_split.contract_code
    ? `${calculationResult.profit_split.contract_code} (Grower ${calculationResult.profit_split.grower_share_pct}% / VSB ${calculationResult.profit_split.vsb_share_pct}%)`
    : `Standard Agreement (Grower ${calculationResult.profit_split.grower_share_pct}% / VSB ${calculationResult.profit_split.vsb_share_pct}%)`;

  const kpis = calculationResult.kpis;
  const avgSaleWeight = kpis.total_sold_heads > 0 ? (kpis.total_sold_net_weight_kg / kpis.total_sold_heads).toFixed(2) : '—';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden max-h-[92vh] flex flex-col bg-slate-100">
        {/* Modal Toolbar (hidden during print) */}
        <DialogHeader className="p-4 bg-slate-900 text-white flex flex-row items-center justify-between border-b border-slate-800 shrink-0 print:hidden">
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-indigo-400" />
            <DialogTitle className="text-sm font-bold text-white tracking-tight">
              Grower Liquidation Statement Preview
            </DialogTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold h-8 px-3 shadow"
            >
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Export PDF
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-400 hover:text-white h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Document Body (Printable Area) */}
        <div className="p-4 sm:p-8 overflow-y-auto flex-1 bg-slate-100 print:p-0 print:m-0 print:overflow-visible print:bg-white">
          <div className="max-w-[800px] mx-auto bg-white border border-slate-300 shadow-sm p-6 sm:p-10 font-sans text-slate-900 print:shadow-none print:border-none print:p-0 print:max-w-none text-xs leading-normal space-y-6">
            
            {/* 1. Header & Organization Branding */}
            <div className="flex justify-between items-start border-b-2 border-slate-900 pb-4">
              <div>
                <h1 className="text-xl font-extrabold tracking-tight text-slate-950">VSB FARM MANAGEMENT</h1>
                <p className="text-xs text-slate-600 font-medium">Sorosoro Ibaba, Batangas City, Philippines</p>
                <p className="text-xs text-slate-500">Contact: operations@vsbfarms.ph</p>
              </div>
              <div className="text-right">
                <div className="text-base font-extrabold tracking-wider text-indigo-950">LIQUIDATION STATEMENT</div>
                <div className="text-xs font-mono font-bold text-slate-700 mt-1">Ref: {settlementNumber}</div>
                <div className="text-xs text-slate-500">Date Generated: {liquidationDate}</div>
              </div>
            </div>

            {/* 2. Grower & Cycle Information */}
            <div className="border border-slate-300 rounded-md overflow-hidden">
              <div className="bg-slate-100 px-3 py-1.5 font-bold text-slate-800 text-[11px] uppercase tracking-wider border-b border-slate-300">
                Grower & Cycle Information
              </div>
              <div className="grid grid-cols-2 p-3 gap-y-1.5 text-xs">
                <div><span className="text-slate-500 font-medium inline-block w-28">Grower Name:</span> <span className="font-bold text-slate-900">{growerName}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-28">Cycle Code:</span> <span className="font-bold text-slate-900">{cycle.cycle_code}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-28">Farm Location:</span> <span className="font-medium text-slate-800">{farmLocation}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-28">Placement Date:</span> <span className="font-medium text-slate-800">{formatDate(cycle.start_date)}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-28">Contract Ref:</span> <span className="font-medium text-slate-800">{contractRef}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-28">Liquidation Date:</span> <span className="font-medium text-slate-800">{liquidationDate}</span></div>
              </div>
            </div>

            {/* 3. Physical Production Metrics */}
            <div className="border border-slate-300 rounded-md overflow-hidden">
              <div className="bg-slate-100 px-3 py-1.5 font-bold text-slate-800 text-[11px] uppercase tracking-wider border-b border-slate-300">
                Physical Production Metrics
              </div>
              <div className="grid grid-cols-2 p-3 gap-y-1.5 text-xs">
                <div><span className="text-slate-500 font-medium inline-block w-32">Pigs Placed:</span> <span className="font-mono font-bold text-slate-900">{kpis.total_placed_heads} head</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-32">Total Feed Used:</span> <span className="font-mono font-bold text-slate-900">{calculationResult.snapshot_data?.physical_summary?.total_feed_bags ?? '—'} bags ({kpis.total_feed_consumed_kg ? `${kpis.total_feed_consumed_kg.toLocaleString()} kg` : '—'})</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-32">Pigs Sold:</span> <span className="font-mono font-bold text-slate-900">{kpis.total_sold_heads} head</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-32">Feed Conversion (FCR):</span> <span className="font-mono font-bold text-slate-900">{kpis.fcr ? `${kpis.fcr.toFixed(2)} FCR` : 'N/A (Measured kg required)'}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-32">Mortality Loss:</span> <span className="font-mono font-bold text-slate-900">{kpis.total_mortality_heads} head {kpis.mortality_rate_pct !== null ? `(${kpis.mortality_rate_pct}%)` : ''}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-32">Average Daily Gain:</span> <span className="font-mono font-bold text-slate-900">{kpis.adg_kg_day ? `${kpis.adg_kg_day.toFixed(3)} kg/day` : 'N/A'}</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-32">Total Net Sale Weight:</span> <span className="font-mono font-bold text-slate-900">{kpis.total_sold_net_weight_kg.toLocaleString()} kg</span></div>
                <div><span className="text-slate-500 font-medium inline-block w-32">Average Sale Weight:</span> <span className="font-mono font-bold text-slate-900">{avgSaleWeight} kg/head</span></div>
              </div>
            </div>

            {/* 4. Financial Reconciliation Summary */}
            <div className="space-y-4">
              <div className="font-bold text-slate-900 text-xs uppercase tracking-wider border-b border-slate-400 pb-1">
                Financial Reconciliation Summary
              </div>

              {/* A. Gross Revenue */}
              <div className="space-y-1">
                <div className="font-bold text-slate-800 flex justify-between">
                  <span>A. GROSS REVENUE (CREDITS)</span>
                </div>
                <div className="pl-4 space-y-0.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Pig Sales Revenue</span>
                    <span className="font-mono text-slate-900">{formatCurrency(calculationResult.total_sales_revenue)}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-1 text-slate-900">
                    <span>TOTAL GROSS CREDITS</span>
                    <span className="font-mono">{formatCurrency(calculationResult.total_sales_revenue)}</span>
                  </div>
                </div>
              </div>

              {/* B. Production Costs */}
              <div className="space-y-1">
                <div className="font-bold text-slate-800 flex justify-between">
                  <span>B. TOTAL PRODUCTION COSTS (DEBITS)</span>
                </div>
                <div className="pl-4 space-y-0.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Piglet Placement Cost</span>
                    <span className="font-mono text-slate-900">{formatCurrency(calculationResult.total_piglet_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Feed Consumed</span>
                    <span className="font-mono text-slate-900">{formatCurrency(calculationResult.total_feed_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Veterinary Medicines & Vaccines (3030)</span>
                    <span className="font-mono text-slate-900">{formatCurrency(calculationResult.total_med_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Hauling, Trucking & Permits (3050 / 3051)</span>
                    <span className="font-mono text-slate-900">{formatCurrency(calculationResult.total_other_costs)}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-1 text-slate-900">
                    <span>TOTAL PRODUCTION DEBITS</span>
                    <span className="font-mono">{formatCurrency(calculationResult.total_production_expenses)}</span>
                  </div>
                </div>
              </div>

              {/* C. Net Cycle Profit & Contract Split */}
              <div className="space-y-1 bg-slate-50 p-2.5 rounded border border-slate-200">
                <div className="flex justify-between font-bold text-slate-900">
                  <span>C. NET CYCLE PROFIT (Gross Credits – Production Debits)</span>
                  <span className="font-mono text-indigo-950">{formatCurrency(calculationResult.gross_profit)}</span>
                </div>
                <div className="pl-4 space-y-0.5 text-xs pt-1 border-t border-slate-200">
                  <div className="flex justify-between">
                    <span className="text-slate-700 font-medium">• Grower Gross Share ({calculationResult.profit_split.grower_share_pct}%)</span>
                    <span className="font-mono font-bold text-slate-900">{formatCurrency(calculationResult.grower_gross_share)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-700 font-medium">• VSB Farm Share ({calculationResult.profit_split.vsb_share_pct}%)</span>
                    <span className="font-mono font-bold text-slate-900">{formatCurrency(calculationResult.vsb_share_amount)}</span>
                  </div>
                </div>
              </div>

              {/* D. Adjustments */}
              <div className="space-y-1">
                <div className="font-bold text-slate-800 flex justify-between">
                  <span>D. GROWER ADJUSTMENTS & REIMBURSEMENTS (Accounts 4010 – 4050)</span>
                </div>
                <div className="pl-4 space-y-0.5 text-xs">
                  {calculationResult.settlement_lines.filter(l => l.line_type === 'adjustment_addition' || l.line_type === 'adjustment_deduction').map(l => (
                    <div key={l.line_number} className="flex justify-between">
                      <span className="text-slate-600">{l.description}</span>
                      <span className={`font-mono ${l.is_deduction ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {l.is_deduction ? '– ' : '+ '}{formatCurrency(l.total_amount)}
                      </span>
                    </div>
                  ))}
                  {calculationResult.settlement_lines.filter(l => l.line_type === 'adjustment_addition' || l.line_type === 'adjustment_deduction').length === 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500 italic">No adjustments applied</span>
                      <span className="font-mono text-slate-900">₱0.00</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-1 text-slate-900">
                    <span>NET ADJUSTMENTS</span>
                    <span className="font-mono">{formatCurrency(calculationResult.net_adjustments)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Final Net Payable Callout */}
            <div className="p-4 bg-slate-950 text-white rounded-lg space-y-1.5 print:bg-slate-950 print:text-white">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase font-extrabold tracking-wider text-slate-300">
                  TOTAL NET PAYABLE TO GROWER:
                </span>
                <span className="text-xl font-mono font-black text-amber-400">
                  {formatCurrency(calculationResult.grower_net_payout)}
                </span>
              </div>
              <div className="text-[11px] text-slate-300 italic border-t border-slate-800 pt-1">
                Amount in Words: <span className="font-semibold text-white">{numberToWords(calculationResult.grower_net_payout)}</span>
              </div>
            </div>

            {/* 6. Approval & Conforme 3-Signatory Block */}
            <div className="pt-6 border-t border-slate-300">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-6">
                Approval & Conforme Signatures
              </div>
              <div className="grid grid-cols-3 gap-6 text-center text-xs">
                <div>
                  <div className="border-b border-slate-800 pb-1 mb-1 font-bold text-slate-900">
                    Operations Specialist
                  </div>
                  <p className="text-[10px] text-slate-500">Prepared By</p>
                  <p className="text-[10px] text-slate-400 mt-1">Date: {liquidationDate}</p>
                </div>
                <div>
                  <div className="border-b border-slate-800 pb-1 mb-1 font-bold text-slate-900">
                    Accounting Supervisor
                  </div>
                  <p className="text-[10px] text-slate-500">Checked & Audited By</p>
                  <p className="text-[10px] text-slate-400 mt-1">Date: {liquidationDate}</p>
                </div>
                <div>
                  <div className="border-b border-slate-800 pb-1 mb-1 font-bold text-slate-900">
                    {growerName}
                  </div>
                  <p className="text-[10px] text-slate-500">Grower Partner (Conforme)</p>
                  <p className="text-[10px] text-slate-400 mt-1">Date: ______________</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer actions */}
        <DialogFooter className="p-3 bg-white border-t border-slate-200 flex justify-end gap-2 shrink-0 print:hidden">
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs">
            Close Preview
          </Button>
          <Button size="sm" onClick={handlePrint} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print Statement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
